import { Hit, SearchForFacetValuesResponse, SearchResponse } from '@algolia/client-search';
import algoliasearch from 'algoliasearch/lite';
import { SearchParametersEntity } from './searchParameters.entity';
import { getFilterTags, getHitsPerPage } from '../utilFuncs/contextAwareUtils';
import { trackEvent } from '../analytics/aemAnalytics';
import { store } from '../stateManager/store';
import { isDealsPage } from '../utilFuncs/dealsManagement';
import { FilterTypeToFacets } from '../../../../interfaces/src/lib/enums/index';
import { Tag } from '../../../../interfaces/src/lib/interfaces/index';
import { SearchRequestParams } from '../../../../interfaces/src/lib/types/index';
import type { Requester } from '@algolia/requester-common';

export interface AlgoliaSDKServiceProps {
  environment: string;
  prefixIndexSearch: string;
}

const getLocaleCodeFromMetaTag = (): string => {
  return document.querySelector('meta[name="localeCode"]')?.getAttribute('content') || 'en_gb';
};

/**
 * Returns the Algolia Application ID from the Redux store.
 *
 * @returns {string} Algolia Application ID if available, otherwise an empty string.
 */
const getAlgoliaApplicationId = (): string => {
  return document.querySelector('meta[name="algoliaAppId"]')?.getAttribute('content') || '';
};

const isProduction = (): boolean => {
  return (
    document.querySelector('meta[name="envType"]')?.getAttribute('content')?.toLowerCase() ===
      'prod' || false
  );
};

/**
 * Returns the Algolia API Key from the Redux store.
 *
 * @returns {string} Algolia API key if available, otherwise an empty string.
 */
const getAlgoliaApiKey = (): string => {
  return document.querySelector('meta[name="algoliaAppKey"]')?.getAttribute('content') || '';
};
export class AlgoliaSDKService {
  private props: AlgoliaSDKServiceProps;
  private client;

  /**
   * This constructor initializes the Algolia client and the Algolia index
   */
  constructor(props: AlgoliaSDKServiceProps) {
    // Initialize the Algolia client
    const protocol = window.location.protocol?.replace(':', '');
    const finalUrl = window.location.host + getSearchBaseAPI();
    this.props = props;

    //TODO code to manage the transiction of the lower env, revert this code when the akamai ticket are done
    const applicationId = getAlgoliaApplicationId();
    const apiKey = getAlgoliaApiKey();
    const hasContextAwareAlgoliaConfig = applicationId.trim() !== '' && apiKey.trim() !== '';

    const noCookieRequester: Requester = {
      send(request) {
        return fetch(request.url, {
          method: request.method,
          headers: request.headers as HeadersInit,
          body: request.data,
          credentials: 'omit',
        }).then(async (response) => ({
          status: response.status,
          content: await response.text(),
          isTimedOut: false,
        }));
      },
    };
    if (isProduction()) {
      this.client = algoliasearch('', '', {
        hosts: [{ url: finalUrl, protocol: protocol }], // Add your custom hosts here
        headers: {
          'Content-Type': 'application/json',
          'MSC-Locale': getLocaleCodeFromMetaTag(),
        },
        timeouts: {
          connect: 7,
          read: 45,
          write: 30,
        },
      });
    } else if (!isProduction() && hasContextAwareAlgoliaConfig) {
      this.client = algoliasearch(applicationId, apiKey, {
        authMode: 1,
        timeouts: {
          connect: 7,
          read: 45,
          write: 30,
        },
        requester: noCookieRequester,
      });
    } else {
      this.client = algoliasearch('', '', {
        hosts: [{ url: finalUrl, protocol: protocol }], // Add your custom hosts here
        headers: {
          'Content-Type': 'application/json',
          'MSC-Locale': getLocaleCodeFromMetaTag(),
        },
        timeouts: {
          connect: 7,
          read: 45,
          write: 30,
        },
      });
    }

    // @ts-expect-error - algolia not offer possibility to skip app id and api key as querystring so is needed to override it in the transporter
    this.client.transporter.queryParameters = {};
  }

  //TODO set correct type and remove any
  private async searchAlgoliaIndex(
    type: string,
    sortBy: string,
    sortOrder: string,
    country: string,
    lang: string,
    priceType: string,
    requestParams: any
  ): Promise<SearchResponse> {
    const finalIndex = this.buildFinalIndex(lang, country, sortBy, sortOrder, type, priceType);
    const index = this.client.initIndex(finalIndex);

    const searchParameters: SearchParametersEntity = new SearchParametersEntity();
    searchParameters.filters = requestParams.filters;
    searchParameters.hitsPerPage = requestParams.hitsPerPage ?? getHitsPerPage() ?? 8;
    searchParameters.page = requestParams.pageFromRequest ?? 0;
    searchParameters.distinct = requestParams.distinct ?? 1;
    searchParameters.attributesToHighlight = [''];
    searchParameters.facets = requestParams.facets ?? ['*'];
    searchParameters.getRankingInfo = true;
    searchParameters.userToken = requestParams.userToken;
    searchParameters.clickAnalytics = requestParams.clickAnalytics || false;
    if (requestParams.context && requestParams.context.length > 0) {
      searchParameters.ruleContexts = requestParams.context;
    }
    //Call the algolia SDK and return the result as a Promise, without and kind of data processing
    return index
      .search('', searchParameters)
      .then((searchResponse) => {
        return searchResponse;
      })
      .catch((error) => {
        console.log('Failed to fetch data from Algolia:', error);
        console.error({
          params: {
            Name: error.name,
            'Message:': error.message,
            service: type,
            algoliaAppId: index.appId,
          },
        });
        trackEvent('error', {
          error_source: 'algolia',
          error_message: error.message ? error.message : '',
          error_code: error.status ? error.status : '',
        });
      }) as Promise<SearchResponse>;
  }

  private buildFinalIndex(
    lang: string,
    country: string,
    sortBy: string,
    sortOrder: string,
    type: string,
    priceType: string
  ) {
    const paxType = store.getState().genericFilter.promotion[0]?.value;
    const paxTypesFromData = store.getState().search.itineraryData?.facets?.['paxTypeTrue'];
    const genericPaxTypesList = paxTypesFromData ? Object.keys(paxTypesFromData) : [];
    const isPaxTypeGeneric = paxType !== undefined && genericPaxTypesList.includes(paxType);
    const dealsPage = isDealsPage();
    let finalIndex = `${this.props.prefixIndexSearch}-${lang}-${country}`;
    const deduplicationSuffix =
      sortBy !== 'best-saving'
        ? sortOrder && type
          ? `-${type}-deduplication-${sortBy}-${sortOrder}`
          : sortBy
          ? `-${type}-deduplication-${sortBy}`
          : `-${type}`
        : '';

    const bestSavingsSuffix =
      sortBy && sortBy === 'best-saving' && priceType
        ? !dealsPage || paxType == undefined || isPaxTypeGeneric
          ? `-${sortBy}-per-${priceType}`
          : `-${sortBy}-${paxType}-per-${priceType}`
        : '';

    finalIndex += deduplicationSuffix + bestSavingsSuffix;
    return finalIndex;
  }

  private async searchAlgoliaIndexMultipleQueries(
    type: string,
    sortBy: string,
    sortOrder: string,
    country: string,
    lang: string,
    priceType: string,
    requestParams: any
  ): Promise<{ results: SearchResponse[] }> {
    const finalIndex = this.buildFinalIndex(lang, country, sortBy, sortOrder, type, priceType);
    const index = this.client.initIndex(finalIndex);
    let queries = [];
    if (requestParams.filters !== undefined) {
      queries = requestParams.filters.map((filter: string, i: number) => {
        const newQuery: any = {
          indexName: index.indexName,
          distinct: requestParams.facets[i] == 'pricerange' ? 0 : 1,
          filters: filter,
          hitsPerPage: requestParams.hitsPerPage[i] ?? 0,
          page: requestParams.hitsPerPage[i] ?? 0,
          facets:
            FilterTypeToFacets[requestParams.facets[i] as keyof typeof FilterTypeToFacets].split(
              ' '
            ),
          attributesToHighlight: [''],
          getRankingInfo: true,
          clickAnalytics: requestParams.clickAnalytics || false,
          userToken: requestParams.userToken,
        };

        if (requestParams.context && requestParams.context.length > 0) {
          newQuery['ruleContexts'] = requestParams.context;
        }

        return newQuery;
      });

      //Call the algolia SDK and return the result as a Promise, without and kind of data processing
      return this.client
        .search(queries)
        .then((results) => {
          return results.results.map(
            (result: SearchResponse | SearchForFacetValuesResponse, i: number) => ({
              filterType: requestParams.facets[i],
              itineraryData: result,
            })
          );
        })
        .catch((error) => {
          console.log('Failed to fetch facets from Algolia:', error);
          console.error({
            params: {
              Name: error.name,
              'Message: ': error.message,
              service: 'facets',
              algoliaAppId: index.appId,
            },
          });
        }) as any;
    } else {
      // is multi cabin for dateslayer
      queries = requestParams.map((params: SearchParametersEntity) => {
        const newQuery: any = {
          indexName: index.indexName,
          distinct: params.distinct ?? 1,
          filters: params.filters,
          hitsPerPage: params.hitsPerPage,
          page: params.pageFromRequest,
          facets: '*',
          attributesToHighlight: [''],
          getRankingInfo: true,
          clickAnalytics: params.clickAnalytics || false,
          userToken: params.userToken,
        };
        if (requestParams.context && requestParams.context.length > 0) {
          newQuery['ruleContexts'] = requestParams.context;
        }

        return newQuery;
      });

      //Call the algolia SDK and return the result as a Promise, without and kind of data processing
      return this.client
        .search(queries)
        .then((results) => {
          const queryID = (results.results[0] as SearchResponse).queryID;
          const indexUsed = (results.results[0] as SearchResponse).indexUsed;
          const combinedHits = results.results.flatMap((result: Hit<any>) => result.hits);
          return {
            queryID,
            indexUsed,
            hits: combinedHits,
          };
        })
        .catch((error) => {
          console.log('Failed to fetch facets from Algolia:', error);
          console.error({
            params: {
              Name: error.name,
              'Message: ': error.message,
              service: 'facets',
              algoliaAppId: index.appId,
            },
          });
        }) as any;
    }
  }

  private async searchAlgoliaIndexMultipleQueriesAir(
    type: string,
    sortBy: string,
    sortOrder: string,
    country: string,
    lang: string,
    priceType: string,
    requestParams: any
  ): Promise<{ results: SearchResponse[] }> {
    const finalIndex = this.buildFinalIndex(lang, country, sortBy, sortOrder, type, priceType);
    const index = this.client.initIndex(finalIndex);
    const queries = [];
    let queriesWithoutFlights = [];
    let queriesWithFlights = [];
    if (requestParams.filters !== undefined) {
      queriesWithoutFlights = requestParams.filters.map((filter: string, i: number) => {
        const newQuery: any = {
          indexName: index.indexName,
          distinct: 1,
          filters: filter.concat(' AND (hasAir: false)'),
          hitsPerPage: requestParams.hitsPerPage[i] ?? 0,
          page: requestParams.hitsPerPage[i] ?? 0,
          facets:
            FilterTypeToFacets[requestParams.facets[i] as keyof typeof FilterTypeToFacets].split(
              ' '
            ),
          attributesToHighlight: [''],
          getRankingInfo: true,
          clickAnalytics: requestParams.clickAnalytics || false,
          userToken: requestParams.userToken,
        };

        if (requestParams.context && requestParams.context.length > 0) {
          newQuery['ruleContexts'] = requestParams.context;
        }

        return newQuery;
      });
      queriesWithFlights = requestParams.filters.map((filter: string, i: number) => {
        const newQuery: any = {
          indexName: index.indexName,
          distinct: 1,
          filters: filter.concat(' AND (hasAir: true)'),
          hitsPerPage: requestParams.hitsPerPage[i] ?? 0,
          page: requestParams.hitsPerPage[i] ?? 0,
          facets:
            FilterTypeToFacets[requestParams.facets[i] as keyof typeof FilterTypeToFacets].split(
              ' '
            ),
          attributesToHighlight: [''],
          getRankingInfo: true,
          clickAnalytics: requestParams.clickAnalytics || false,
          userToken: requestParams.userToken,
        };

        if (requestParams.context && requestParams.context.length > 0) {
          newQuery['ruleContexts'] = requestParams.context;
        }

        return newQuery;
      });
      queries.push(...queriesWithoutFlights);
      queries.push(...queriesWithFlights);

      //Call the algolia SDK and return the result as a Promise, without and kind of data processing
      return this.client
        .search(queries)
        .then((results) => {
          return results.results.map(
            (result: SearchResponse | SearchForFacetValuesResponse, i: number) => ({
              filterType: requestParams.facets[i],
              itineraryData: result,
            })
          );
        })
        .catch((error) => {
          console.log('Failed to fetch facets from Algolia:', error);
          console.error({
            params: {
              Name: error.name,
              'Message: ': error.message,
              service: 'facets',
              algoliaAppId: index.appId,
            },
          });
        }) as any;
    } else {
      // is multi cabin for dateslayer
      queriesWithoutFlights = requestParams.map((params: SearchParametersEntity) => {
        // Add filters to the copied objects
        const filtersWithoutFlights =
          (this.deleteAllFlightFilterTags(params) || '') + ' AND (hasAir: false)';

        const newQuery: any = {
          indexName: index.indexName,
          distinct: params.distinct ?? 1,
          filters: filtersWithoutFlights,
          hitsPerPage: params.hitsPerPage,
          page: params.pageFromRequest,
          facets: '*',
          attributesToHighlight: [''],
          getRankingInfo: true,
          clickAnalytics: params.clickAnalytics || false,
          userToken: params.userToken,
        };

        if (requestParams.context && requestParams.context.length > 0) {
          newQuery['ruleContexts'] = requestParams.context;
        }
        return newQuery;
      });

      queriesWithFlights = requestParams.map((params: SearchParametersEntity) => {
        // Create deep copies of searchParameters
        const filters = JSON.parse(JSON.stringify(params.filters));

        // Add filters to the copied objects
        const filtersWithoutFlights = (filters || '') + ' AND (hasAir: true)';

        const newQuery: any = {
          indexName: index.indexName,
          distinct: params.distinct ?? 1,
          filters: filtersWithoutFlights,
          hitsPerPage: params.hitsPerPage,
          page: params.pageFromRequest,
          facets: '*',
          attributesToHighlight: [''],
          getRankingInfo: true,
          clickAnalytics: params.clickAnalytics || false,
          userToken: params.userToken,
        };

        if (requestParams.context && requestParams.context.length > 0) {
          newQuery['ruleContexts'] = requestParams.context;
        }
        return newQuery;
      });
      queries.push(...queriesWithoutFlights);
      queries.push(...queriesWithFlights);
      //Call the algolia SDK and return the result as a Promise, without and kind of data processing
      return this.client
        .search(queries)
        .then((results) => {
          const combinedHits = results.results.flatMap((result: Hit<any>) => result.hits);
          const queryID = (results.results[0] as SearchResponse).queryID;
          const indexUsed = (results.results[0] as SearchResponse).indexUsed;
          return {
            queryID,
            indexUsed,
            hits: combinedHits,
          };
        })
        .catch((error) => {
          console.log('Failed to fetch facets from Algolia:', error);
          console.error({
            params: {
              Name: error.name,
              'Message: ': error.message,
              service: 'facets',
              algoliaAppId: index.appId,
            },
          });
        }) as any;
    }
  }

  private deleteAllFlightFilterTags = (searchParameters: SearchParametersEntity): string => {
    const filterTagsWithFlights = getFilterTags().filter((tag: Tag) => tag.hasAir);
    let filtersWithoutFlightsTags = searchParameters.filters;
    for (const tag of filterTagsWithFlights) {
      const tagValue = `filterTags:"${tag.tagValue}"`;
      const tagValueOrRight = `${tagValue} OR`;
      const tagValueOrLeft = `OR ${tagValue}`;
      filtersWithoutFlightsTags = filtersWithoutFlightsTags
        .replace(tagValueOrRight, '')
        .replace(tagValueOrLeft, '')
        .replace(tagValue, '')
        .replace('AND ()', '')
        .replace('() AND', '')
        .replace('()', '');
    }
    return filtersWithoutFlightsTags;
  };

  private async searchAlgoliaIndexHasAir(
    type: string,
    sortBy: string,
    sortOrder: string,
    country: string,
    lang: string,
    priceType: string,
    requestParams: any
  ): Promise<SearchResponse> {
    const finalIndex = this.buildFinalIndex(lang, country, sortBy, sortOrder, type, priceType);
    const index = this.client.initIndex(finalIndex);
    const queries = [];
    const searchParameters: SearchParametersEntity = new SearchParametersEntity();
    searchParameters.indexName = index.indexName;
    searchParameters.params = '';
    searchParameters.filters = requestParams.filters;
    searchParameters.hitsPerPage = requestParams.hitsPerPage ?? getHitsPerPage() ?? 8;
    searchParameters.page = requestParams.pageFromRequest ?? 0;
    searchParameters.distinct = requestParams.distinct ?? 1;
    searchParameters.attributesToHighlight = [''];
    searchParameters.facets = requestParams.facets ?? ['*'];
    searchParameters.getRankingInfo = true;
    searchParameters.userToken = requestParams.userToken;
    searchParameters.clickAnalytics = requestParams.clickAnalytics || false;

    if (requestParams.context && requestParams.context.length > 0) {
      searchParameters.ruleContexts = requestParams.context;
    }

    // Create deep copies of searchParameters
    const searchParameters1 = JSON.parse(JSON.stringify(searchParameters));
    const searchParameters2 = JSON.parse(JSON.stringify(searchParameters));
    const searchParameters3 = JSON.parse(JSON.stringify(searchParameters));

    // Add filters to the copied objects
    searchParameters1.filters = this.deleteAllFlightFilterTags(searchParameters1) || '';
    searchParameters2.filters =
      (searchParameters2.filters || '') +
      ' AND (hasAir: true)' +
      ' AND (prices.availability: true)';

    searchParameters3.filters =
      (this.deleteAllFlightFilterTags(searchParameters3) || '') +
      ' AND (hasAir: false)' +
      ' AND (prices.availability: true)';

    const searchParametersDefault: SearchParametersEntity = searchParameters1;
    const searchParametersHasAirTrue: SearchParametersEntity = searchParameters2;
    const searchParametersHasAirFalse: SearchParametersEntity = searchParameters3;

    // Construct query objects
    const query1: any = { ...searchParametersDefault };
    const query2: any = { ...searchParametersHasAirTrue };
    const query3: any = { ...searchParametersHasAirFalse };
    // Push the queries into the queries array
    queries.push(query1);
    queries.push(query2);
    queries.push(query3);
    //Call the algolia SDK and return the result as a Promise, without and kind of data processing
    return this.client
      .search(queries)
      .then((results) => {
        return {
          hits: results,
        };
      })
      .catch((error) => {
        console.log('Failed to fetch facets from Algolia:', error);
        console.error({
          params: {
            Name: error.name,
            'Message: ': error.message,
            service: 'facets',
            algoliaAppId: index.appId,
          },
        });
      }) as any;
  }

  private async searchAlgoliaIndexHasAirDateslayer(
    type: string,
    sortBy: string,
    sortOrder: string,
    country: string,
    lang: string,
    priceType: string,
    requestParams: any
  ): Promise<SearchResponse> {
    const finalIndex = this.buildFinalIndex(lang, country, sortBy, sortOrder, type, priceType);
    const index = this.client.initIndex(finalIndex);
    const queries = [];
    const searchParameters: SearchParametersEntity = new SearchParametersEntity();
    searchParameters.indexName = index.indexName;
    searchParameters.params = '';
    searchParameters.filters = requestParams.filters;
    searchParameters.hitsPerPage = requestParams.hitsPerPage ?? getHitsPerPage() ?? 8;
    searchParameters.page = requestParams.pageFromRequest ?? 0;
    searchParameters.distinct = requestParams.distinct ?? 1;
    searchParameters.attributesToHighlight = [''];
    searchParameters.facets = requestParams.facets ?? ['*'];
    searchParameters.getRankingInfo = true;
    searchParameters.userToken = requestParams.userToken;
    searchParameters.clickAnalytics = requestParams.clickAnalytics || false;

    if (requestParams.context && requestParams.context.length > 0) {
      searchParameters.ruleContexts = requestParams.context;
    }

    // Create deep copies of searchParameters
    const searchParameters1 = JSON.parse(JSON.stringify(searchParameters));
    const searchParameters2 = JSON.parse(JSON.stringify(searchParameters));
    const searchParameters3 = JSON.parse(JSON.stringify(searchParameters));

    // Add filters to the copied objects
    searchParameters1.filters = this.deleteAllFlightFilterTags(searchParameters1) || '';
    searchParameters2.filters = (searchParameters2.filters || '') + ' AND (hasAir: true)';
    searchParameters3.filters =
      (this.deleteAllFlightFilterTags(searchParameters3) || '') + ' AND (hasAir: false)';

    const searchParametersDefault: SearchParametersEntity = searchParameters1;
    const searchParametersHasAirTrue: SearchParametersEntity = searchParameters2;
    const searchParametersHasAirFalse: SearchParametersEntity = searchParameters3;

    // Construct query objects
    const query1: any = { ...searchParametersDefault };
    const query2: any = { ...searchParametersHasAirTrue };
    const query3: any = { ...searchParametersHasAirFalse };
    // Push the queries into the queries array
    queries.push(query1);
    queries.push(query2);
    queries.push(query3);
    //Call the algolia SDK and return the result as a Promise, without and kind of data processing
    return this.client
      .search(queries)
      .then((results) => {
        const queryID = (results.results[0] as SearchResponse).queryID;
        const indexUsed = (results.results[0] as SearchResponse).indexUsed;
        const combinedHits = results.results.flatMap((result: Hit<any>) => result.hits);
        return {
          queryID,
          indexUsed,
          hits: combinedHits,
        };
      })
      .catch((error) => {
        console.log('Failed to fetch facets from Algolia:', error);
        console.error({
          params: {
            Name: error.name,
            'Message: ': error.message,
            service: 'facets',
            algoliaAppId: index.appId,
          },
        });
      }) as any;
  }

  async getItinerariesFromAlgoliaV4(
    requestParams: SearchRequestParams,
    sortBy: string,
    sortOrder: string,
    country: string,
    lang: string,
    priceType: string
  ): Promise<SearchResponse> {
    return this.searchAlgoliaIndex(
      'itinerary',
      sortBy,
      sortOrder,
      country,
      lang,
      priceType,
      requestParams
    );
  }

  async getItinerariesFromAlgoliaV4Air(
    requestParams: SearchRequestParams,
    sortBy: string,
    sortOrder: string,
    country: string,
    lang: string,
    priceType: string
  ): Promise<SearchResponse> {
    return this.searchAlgoliaIndexHasAirDateslayer(
      'itinerary',
      sortBy,
      sortOrder,
      country,
      lang,
      priceType,
      requestParams
    );
  }

  /**
   * This method fetches cruises from Algolia using the Algolia API
   * @param requestParams list of all parameters useful for search
   * @param country country criteria passed through the request
   * @param lang language criteria passed through the request
   * @returns list of cabins that corresponds to the search
   */
  async getCruisesFromAlgoliaV1(
    requestParams: SearchRequestParams,
    country: string,
    lang: string
  ): Promise<SearchResponse> {
    return this.searchAlgoliaIndex('cruises', '', '', country, lang, '', requestParams);
  }

  async getCruisesFromAlgoliaV1Air(
    requestParams: SearchRequestParams,
    country: string,
    lang: string
  ): Promise<SearchResponse> {
    return this.searchAlgoliaIndexHasAir('cruises', '', '', country, lang, '', requestParams);
  }

  async getMultipleItinerariesFromAlgoliaV1(
    requestParams: SearchRequestParams,
    sortBy: string,
    sortOrder: string,
    country: string,
    lang: string,
    priceType: string
  ): Promise<{ results: SearchResponse[] }> {
    return this.searchAlgoliaIndexMultipleQueries(
      'itinerary',
      sortBy,
      sortOrder,
      country,
      lang,
      priceType,
      requestParams
    );
  }

  async getMultipleItinerariesFromAlgoliaV1Air(
    requestParams: SearchRequestParams,
    sortBy: string,
    sortOrder: string,
    country: string,
    lang: string,
    priceType: string
  ): Promise<{ results: SearchResponse[] }> {
    return this.searchAlgoliaIndexMultipleQueriesAir(
      'itinerary',
      sortBy,
      sortOrder,
      country,
      lang,
      priceType,
      requestParams
    );
  }

  async getExcursionsFromAlgoliaV1(
    requestParams: SearchRequestParams,
    country: string,
    lang: string
  ): Promise<SearchResponse> {
    return this.searchAlgoliaIndex('excursions', '', '', country, lang, '', requestParams);
  }
}

export function getSearchBaseAPI(): string {
  const searchBaseApiMetaTag: HTMLMetaElement = document.querySelector(
    'meta[name="searchBaseApi"]'
  ) as HTMLMetaElement;
  return searchBaseApiMetaTag?.content ?? '';
}
